import { ApolloLink, createHttpLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import type { useTranslations } from 'next-intl';
import type { FanExtensionErrorCode } from '@/__generated__/graphql';
import type { ModalContentType } from '@/components/layout/modals/types';
import { ERROR_MAP } from '@/lib/apollo/error-map';
import { EXCLUDED_MUTATION_OPERATION_NAMES } from '@/lib/apollo/excluded-operation-name';
import { PAYLOAD_ERROR_MAP } from '@/lib/apollo/payload-error-map';
import { ngWordErrorDialog } from '@/lib/apollo/utils/ng-word-error-dialog';
import { ensureIdpTokenFresh } from '@/lib/idp-token-gate';
import { findErrorOrSuccessType, isErrorType } from '@/utils/find-error-or-success-type';

// メインバックエンド向け。NEXT_PUBLIC_API_ORIGIN は環境ごとに解決される
// （dev→dev.klew.jp / stg→stg.klew.jp / prd→klew.jp / idp→idp.klew.jp）。
// client / server で同一定義のため共通の httpLink を使う。
export const httpLink = createHttpLink({
  uri: `${process.env.NEXT_PUBLIC_API_ORIGIN}/query`,
  credentials: 'include',
  fetchOptions: {
    // Cacheを利用しない
    cache: 'no-store',
  },
});

// LIVENODE IdP バックエンド専用。移行フロー(resolveIdpSignIn 等)で使用する。
// idp 環境のみ専用オリジン(idp.klew.jp)を指定し、dev/stg/prd では未設定のため
// 各環境の NEXT_PUBLIC_API_ORIGIN(dev/stg/prd 本体API)へフォールバックする。
const idpApiOrigin = process.env.NEXT_PUBLIC_API_ORIGIN_IDP || process.env.NEXT_PUBLIC_API_ORIGIN;
export const idpHttpLink = createHttpLink({
  uri: `${idpApiOrigin}/query`,
  credentials: 'include',
  fetchOptions: {
    // Cacheを利用しない
    cache: 'no-store',
  },
});

export const payloadErrorLink = (
  showDialog: (message: ModalContentType) => void,
  t: ReturnType<typeof useTranslations>,
) =>
  new ApolloLink((operation, forward) => {
    return forward(operation).map((response) => {
      if (response.data) {
        // 除外リストに含まれるoperationNameの場合は共通エラーハンドリングをスキップ
        if (EXCLUDED_MUTATION_OPERATION_NAMES.includes(operation.operationName)) {
          return response;
        }
        const result = findErrorOrSuccessType(response.data);
        if (!result) return response;
        if (isErrorType(result)) {
          if (PAYLOAD_ERROR_MAP(result, t)) {
            // Ngワード判定
            if (result.__typename === 'ErrorValidationFailed') {
              ngWordErrorDialog(result, showDialog, t);
            } else {
              showDialog(PAYLOAD_ERROR_MAP(result, t));
            }
          } else {
            showDialog(ERROR_MAP('INTERNAL', t));
          }
        }
      }

      return response;
    });
  });

export const globalErrorLink = (
  showDialog: (message: ModalContentType) => void,
  t: ReturnType<typeof useTranslations>,
) =>
  onError(({ graphQLErrors, networkError, operation }) => {
    const operationDefinition = operation.query.definitions?.[0];
    // Pagination実行時は共通エラーハンドリングをスキップ
    if ('after' in operation.variables || 'before' in operation.variables) {
      return;
    }

    const isServer = typeof window === 'undefined';

    graphQLErrors?.forEach((i) => {
      switch (i.extensions?.code as FanExtensionErrorCode) {
        case 'Internal':
          showDialog(ERROR_MAP('INTERNAL', t));
          break;
        case 'NotFound':
          if (operationDefinition && operationDefinition.kind === 'OperationDefinition') {
            const operationType = operationDefinition.operation; // 'query', 'mutation', 'subscription'のいずれか
            if (operationType === 'mutation') {
              showDialog(ERROR_MAP('UNAUTHORIZED', t));
            }
          }
          break;
        case 'OpenseaInternal':
          showDialog(ERROR_MAP('OPEN_SEA_INTERNAL', t));
          break;
        case 'Unauthorized':
          showDialog(ERROR_MAP('UNAUTHORIZED', t));
          break;
        default:
          if (!isServer) {
            showDialog(ERROR_MAP('INTERNAL', t));
          }
      }
    });
    if (networkError && !isServer) {
      showDialog(ERROR_MAP('NETWORK', t));
    }
  });

export const serverErrorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors)
    graphQLErrors.forEach(({ message, locations, path }) => {
      console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);
    });
  if (networkError) {
    console.error(`[Network error]: ${networkError}`);
  }
});

export const responseLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((res) => {
    if (typeof window === 'undefined') {
      console.log(`${operation.operationName}`);
    }
    return res;
  });
});

type IamTarget = 'default' | 'idp';

const resolveIamConfig = (target: IamTarget) =>
  target === 'idp'
    ? {
        route: '/frontend-api/google-auth?target=idp',
        keyFile: process.env.GOOGLE_APPLICATION_CREDENTIALS_IDP,
        audience: process.env.NEXT_PUBLIC_TARGET_AUDIENCE_IDP,
      }
    : {
        route: '/frontend-api/google-auth',
        keyFile: process.env.GOOGLE_APPLICATION_CREDENTIALS,
        audience: process.env.NEXT_PUBLIC_TARGET_AUDIENCE,
      };

export const authLink = (cookieHeader?: string, target: IamTarget = 'default') => {
  const iam = resolveIamConfig(target);
  return setContext(async (_, { headers }) => {
    if (typeof window !== 'undefined' && target === 'default') {
      await ensureIdpTokenFresh();
    }

    let authHeader = {};

    // local のクライアント側: google-auth ルート経由で IAM トークンを取得する。
    // SSR(typeof window === 'undefined') では下の直接発行ブロックが担うため、ここは
    // クライアント限定にする（両方走ると local SSR でトークンを二重発行してしまう）。
    if (
      typeof window !== 'undefined' &&
      process.env.NEXT_PUBLIC_ENVIRONMENT === 'local' &&
      process.env.NEXT_PUBLIC_ENABLE_IAM === 'true'
    ) {
      // 取得失敗は握りつぶさず Apollo 側へ伝播させる（呼び出し元でハンドリング）。
      const data = await fetch(`${process.env.NEXT_PUBLIC_BASE_URL}${iam.route}`, {
        cache: 'no-store',
      });
      if (data.ok) {
        authHeader = await data.json();
      }
    }

    if (
      typeof window === 'undefined' &&
      process.env.NEXT_PUBLIC_ENABLE_IAM === 'true' &&
      process.env.NEXT_PUBLIC_API_MOCKING !== 'enabled'
    ) {
      // 取得失敗は握りつぶさず Apollo 側へ伝播させる（呼び出し元でハンドリング）。
      const { GoogleAuth } = await import('google-auth-library');
      const auth = new GoogleAuth({
        keyFile: iam.keyFile,
      });
      const client = await auth.getIdTokenClient(iam.audience);
      const headers = await client.getRequestHeaders();
      authHeader = headers;
    }

    return {
      headers: {
        ...headers,
        ...authHeader,
        cookie: cookieHeader,
      },
    };
  });
};
